import React, { useEffect, useRef, useState } from "react";
import { Camera, RotateCcw, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, authFileUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";

// Recognition only — becomes the carrier's profile photo shown to the sender after acceptance. No face matching.
export const SelfieCapture = ({ onDone }) => {
  const { user, refresh } = useAuth();
  const video = useRef(null);
  const stream = useRef(null);
  const [live, setLive] = useState(false);
  const [shot, setShot] = useState(null);
  const [busy, setBusy] = useState(false);

  const stop = () => { stream.current?.getTracks().forEach((t) => t.stop()); stream.current = null; setLive(false); };
  useEffect(() => stop, []);

  const open = async () => {
    try {
      stream.current = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: false });
      setLive(true); setShot(null);
      setTimeout(() => { if (video.current) { video.current.srcObject = stream.current; video.current.play(); } }, 0);
    } catch { toast.error("Camera unavailable — use 'Choose photo' instead."); }
  };
  const capture = () => {
    const v = video.current; const c = document.createElement("canvas");
    c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext("2d").drawImage(v, 0, 0);
    c.toBlob((b) => { setShot({ blob: b, url: URL.createObjectURL(b) }); stop(); }, "image/jpeg", 0.85);
  };
  const pick = (file) => setShot({ blob: file, url: URL.createObjectURL(file) });
  const save = async () => {
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", new File([shot.blob], "selfie.jpg", { type: "image/jpeg" }));
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post("/users/me/selfie", { file_id: r.data.file_id });
      toast.success("Profile photo saved");
      setShot(null); await refresh(); onDone?.();
    } catch (e) { toast.error(apiError(e, "Couldn't save photo")); }
    setBusy(false);
  };

  return (
    <div className="space-y-2" data-testid="selfie-capture">
      <div className="flex items-center gap-3">
        <div className="w-20 h-20 rounded-2xl overflow-hidden bg-slate-100 border border-slate-200 grid place-items-center shrink-0">
          {live ? <video ref={video} playsInline muted className="w-full h-full object-cover -scale-x-100" data-testid="selfie-video"/>
            : shot ? <img src={shot.url} alt="Selfie preview" className="w-full h-full object-cover" data-testid="selfie-preview"/>
            : user?.selfie_file_id ? <img src={authFileUrl(user.selfie_file_id)} alt="Your profile" className="w-full h-full object-cover" data-testid="selfie-current"/>
            : <Camera className="w-6 h-6 text-slate-400"/>}
        </div>
        <div className="flex flex-wrap gap-2">
          {live && <Button data-testid="selfie-capture-btn" onClick={capture} className="rounded-full"><Camera className="w-4 h-4 mr-1"/> Capture</Button>}
          {!live && !shot && <Button data-testid="selfie-open-camera-btn" onClick={open} variant="outline" className="rounded-full"><Camera className="w-4 h-4 mr-1"/> {user?.selfie_file_id ? "Retake" : "Take selfie"}</Button>}
          {shot && <Button data-testid="selfie-save-btn" onClick={save} disabled={busy} className="rounded-full">{busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <><Check className="w-4 h-4 mr-1"/> Use this photo</>}</Button>}
          {shot && <Button data-testid="selfie-retake-btn" onClick={open} variant="outline" className="rounded-full"><RotateCcw className="w-4 h-4 mr-1"/> Retake</Button>}
          {!live && (
            <label className="text-xs underline text-slate-500 cursor-pointer self-center">
              Choose photo
              <input type="file" accept="image/*" capture="user" className="hidden" data-testid="selfie-file-input"
                     onChange={(e) => e.target.files?.[0] && pick(e.target.files[0])}/>
            </label>
          )}
        </div>
      </div>
      <p className="text-[11px] text-slate-500">Shown to the sender after they accept, so they know who to hand the parcel to.</p>
    </div>
  );
};

export default SelfieCapture;
